import { AfterViewInit, Component, Injector, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TicketDto } from '@timechef/core/api';
import { AuthRepository } from '@timechef/core/auth';
import { getIdleRequestStatus } from '@timechef/core/utils/elf-utils';
import { Subject, filter } from 'rxjs';
import { Manipulation, Navigation, Pagination, default as Swiper, default as SwiperCore } from 'swiper';
import { SwiperComponent } from 'swiper/angular';
import { PageInfoWithData } from '../../../state/history.models';
import { HistoryRepository } from '../../../state/history.repository';
import { HistoryService } from '../../../state/history.service';

SwiperCore.use([Navigation, Pagination, Manipulation]);
const REVERSE_ARROW_AT = 100;

@UntilDestroy()
@Component({
  selector: 'app-tickets-modal',
  templateUrl: './tickets-modal.component.html',
  styleUrls: ['./tickets-modal.component.scss'],
  standalone: false,
})
export class TicketsModalComponent implements OnInit, AfterViewInit {
  @Input() ticketId?: number;

  @ViewChild(SwiperComponent) swiper?: SwiperComponent;
  tickets?: PageInfoWithData<TicketDto>;

  initialSlide = 0;
  hasSetInitialSlide = false;

  userFullName: string;

  swipeXComplete = new Subject<number>();
  reverseArrow = false;

  private historyService: HistoryService;

  generatePdfRequestStatus = getIdleRequestStatus();
  constructor(
    private modalCtrl: ModalController,
    private historyRepo: HistoryRepository,
    injector: Injector,
    private zone: NgZone,
  ) {
    this.historyService = injector.get(HistoryService);
    this.userFullName = injector.get(AuthRepository).getUserFullName();
  }

  ngOnInit() {
    this.historyRepo.generatePdfRequestStatus$.subscribe((s) => {
      this.generatePdfRequestStatus = s;
    });
    this.historyRepo.ticketsPageInfoWithData$.pipe(untilDestroyed(this)).subscribe((data) => {
      this.zone.run(() => {
        this.tickets = data;

        if (!this.ticketId || this.hasSetInitialSlide) return;

        this.initialSlide = this.tickets.data.findIndex((r) => r.id === this.ticketId);
        this.hasSetInitialSlide = true;
      });

      this.swiper?.swiperRef.update();
    });

    // used to
    this.swipeXComplete
      .pipe(
        filter((_x) => this.tickets!.hasMore),
        untilDestroyed(this),
      )
      .subscribe((_x) => {
        if (this.reverseArrow) {
          this.zone.run(() => {
            this.reverseArrow = false;
          });

          if (this.historyRepo.getTicketsRequestStatus().value !== 'pending') this.historyService.getTickets(this.tickets!.currentPageIndex + 1).subscribe();
        }
      });
  }

  getTicketPdf() {
    if (this.generatePdfRequestStatus.value === 'pending') return;

    this.historyService.getTicketPdfByIndex(this.swiper!.swiperRef.activeIndex).subscribe();
  }

  async dismissModal() {
    await this.modalCtrl.dismiss();
  }

  ngAfterViewInit() {
    if (this.swiper) {
      this.swiper.s_sliderMove.subscribe(([swiper, _ev]) => {
        const computeSwiperX = this.computeSwiperX(swiper);

        const rev = computeSwiperX > REVERSE_ARROW_AT;
        if (this.reverseArrow !== rev) {
          this.zone.run(() => {
            this.reverseArrow = rev;
          });
        }
      });

      this.swiper.s_touchEnd.subscribe(([swiper, _ev]) => {
        const computeSwiperX = this.computeSwiperX(swiper);
        this.swipeXComplete.next(computeSwiperX);
      });
    }
  }

  private computeSwiperX(swiper: Swiper) {
    const internalSwiper = swiper as any;
    const endX = internalSwiper.virtualSize - internalSwiper.slidesSizesGrid[0];
    return -(endX - (Math.abs(swiper.translate) + 20));
  }

  transformtValue(value: number) {
    return Math.abs(value);
  }
}
